import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { getValue } from 'y4/Utils';

let user_inputing = null;

/**
 * DateFilterItem component renders a date range filter item for the FilterPanel.
 * It allows users to select a date range for filtering.
 *
 * @param {*} param
 * @returns
 */
const DateFilterItem = ({ filter, values, changed }) => {
  const { t } = useTranslation('filter');

  const [info, setInfo] = useState({});

  const onChange = dates => {
    if (user_inputing) return;
    const [start, end] = dates;
    const f = start ? format(start, 'yyyy-MM-dd') + ' 00:00:00' : '';
    const t = end ? format(end, 'yyyy-MM-dd') + ' 23:59:59' : '';

    const ex = `${f}~${t}`;
    const dic = {};
    dic[filter.name] = ex == '~' ? null : ex;
    changed(dic);
  };

  const onChangeRaw = t => {
    if (t) {
      user_inputing = t;
      setInfo({ start: info.start, end: info.end, value: t });
    }
  };

  const onCalendarClose = () => {
    if (user_inputing) {
      const i = {};
      const vs = _.split(user_inputing, '~');
      const sd = Date.parse(vs[0].trim());
      i.start = sd;
      if (vs.length > 1) {
        i.end = Date.parse(vs[1].trim());
      }
      user_inputing = null;
      onChange([i.start, i.end]);
    }
  };

  const getValueString = (start, end) => {
    const sd = _.isString(start) ? Date.parse(start) : start;
    const ed = _.isString(start) ? Date.parse(end) : end;
    const sd_f = sd ? format(sd, 'yyyy-MM-dd') : '';
    const ed_f = ed ? format(ed, 'yyyy-MM-dd') : '';
    if (start || end) {
      return `${sd_f} ~ ${ed_f}`;
    }
  };

  useEffect(() => {
    const between = _.get(values, filter.name);
    const from_to = between ? _.split(between, '~') : null;

    const sd = getValue(() => {
      let s = null;
      if (from_to) s = from_to[0];
      if (s) return Date.parse(s);
      return null;
    });
    const ed = getValue(() => {
      let s = null;
      if (from_to && from_to.length > 1) s = from_to[1];
      if (s) return Date.parse(s);
      return null;
    });
    setInfo({ start: sd, end: ed, value: getValueString(sd, ed) });
  }, [values]);

  return (
    <DatePicker
      startDate={info.start}
      endDate={info.end}
      value={info.value}
      selected={info.start}
      onChange={onChange}
      onCalendarClose={onCalendarClose}
      onChangeRaw={e => onChangeRaw(e.target.value)}
      placeholderText={filter.title}
      formatWeekDay={day => {
        return t(day.toString().slice(0, 3));
      }}
      selectsRange
      dateFormat={'yyyy-MM-dd'}
      className="form-control"
      popperClassName="z-index-3"
      isClearable={true}
      monthsShown={2}
      showWeekNumbers={true}
      renderCustomHeader={({
        monthDate,
        customHeaderCount,
        decreaseMonth,
        increaseMonth
      }) => (
        <div>
          <button
            aria-label={t('Previous Month')}
            className={
              'react-datepicker__navigation react-datepicker__navigation--previous'
            }
            style={customHeaderCount === 1 ? { visibility: 'hidden' } : null}
            onClick={decreaseMonth}
          >
            <span
              className={
                'react-datepicker__navigation-icon react-datepicker__navigation-icon--previous'
              }
            >
              {'<'}
            </span>
          </button>
          <span className="react-datepicker__current-month">
            {monthDate.toLocaleString(i18n.language, {
              month: 'long',
              year: 'numeric'
            })}
          </span>
          <button
            aria-label={t('Next Month')}
            className={
              'react-datepicker__navigation react-datepicker__navigation--next'
            }
            style={customHeaderCount === 0 ? { visibility: 'hidden' } : null}
            onClick={increaseMonth}
          >
            <span
              className={
                'react-datepicker__navigation-icon react-datepicker__navigation-icon--next'
              }
            >
              {'>'}
            </span>
          </button>
        </div>
      )}
    />
  );
};

DateFilterItem.propTypes = {
  filter: PropTypes.object,
  values: PropTypes.object,
  changed: PropTypes.func
};

export default DateFilterItem;
